import WappIcono from "../../../components/wappIcono";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { nombreLista, proximaTexto } from "./crmLista";
import { interesesCortos } from "../../../utils/cursoCorto";
import { useSelector } from "react-redux";

import { RootState } from "../../../globals";
import HttpManager from "../../../services/HttpManager";
import { telefonoConLada } from "../../../utils/telefonoBonito";
import { NumeroWa, ProspectCatalogs, ProspectRow } from "../../../services/v2/prospects.service";
import ClienteView from "./clienteView";
import { MensajesEncontrados } from "./buscarMensajes";
import { CarteraSelect } from "./carteraView";
import NewProspectModal from "./newProspectModal";
import NuevoChat, { BotonNuevoChat } from "./nuevoChat";
import EtapasPicker, { etapaParam, TODAS_ETAPAS } from "./etapasPicker";
import { push } from "./push";
import { listoParaBuscar } from "./salesHelpers";
import { quienSoy } from "../../../appMovil/contadores";
import PlantelSelect from "./plantelSelect";
import NumeroSelect, { idsDeNumero } from "./numeroSelect";
import {
	cuando, errorTexto, ETAPAS, etiquetaFiltros, filtrosDeEtapas, iniciales, nombreCompleto, OBJETIVOS_BARRA, resumenWapp, statusClase, tonoAvatar, ultimoMensaje, vistaPrevia, vencido
} from "./salesHelpers";
import "./sales.css";
/** En la lista la próxima actividad va solo con el día ("Llamar · Mañana"): la hora se ve dentro de la conversación. */
const diaMx = (at: string | null | undefined): string | null => {
	if (!at) return null;
	const t = new Date(at.replace(" ", "T") + (/[zZ]|[+-]\d\d:?\d\d$/.test(at) ? "" : "Z")).getTime() - 6 * 3600000;
	return isNaN(t) ? at : new Date(t).toISOString().slice(0, 10);
};

/** Como WhatsApp: una tanda chica que sale al instante y la siguiente al bajar. */
const POR_TANDA = 50;
/** El cliente escribió y nadie le ha contestado después (el punto verde de WhatsApp). */
const sinContestar = (r: ProspectRow) => !!r.last_inbound_at && (!r.last_outbound_at || r.last_outbound_at < r.last_inbound_at);

/** Lo último que se vio de cada lista en esta pestaña: al volver de Equipo o del CRM se pinta al momento y luego se refresca. */
const memoria = new Map<string, { rows: ProspectRow[]; hasMore: boolean }>();

const leer = (k: string, def: string): string => { try { return localStorage.getItem(k) || def; } catch { return def; } };
const guardar = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* sin almacenamiento */ } };

/** Área de WappChat (Mis clientes, Caja, Kits…) cuando la persona tiene más de una. */
export interface AreaOpcion { key: string; label: string; n?: number }
/** Ancho del selector de área según el texto elegido: el navegador lo calculaba corto y cortaba "Mis clientes" en celular. */
export const anchoArea = (areas: AreaOpcion[] | undefined, key?: string | null): string => {
	const a = (areas || []).find((x) => x.key === key);
	const texto = a ? a.label + (a.n ? " · " + a.n.toLocaleString("es-MX") : "") : "";
	return `calc(${(texto.length * 0.47).toFixed(2)}em + 44px)`;
};

interface Props {
	/** Abierta desde el CRM ("Ver en WappChat"): arranca con esta persona. */
	inicial?: number | null;
	/** "Ver en CRM" en la ficha: la misma persona en la vista tipo Zoho. */
	onVerCrm?: (id: number) => void;
	/** La persona abierta cambió (WappChat la pone en la URL). */
	onSel?: (id: number | null) => void;
	/** Selector de área arriba de la lista (solo si tiene más de una). */
	areas?: AreaOpcion[];
	area?: string;
	onArea?: (key: string) => void;
	/** Filtro "Número" (solo Super Admin): los números de Ventas y Admisiones. */
	numeros?: NumeroWa[] | null;
}

/** Conteos que abarcan casi todo el alcance (ProspectService::CONTEOS_PESADOS): se piden aparte, después de los rápidos. */
const CONTEOS_PESADOS = ["total", "etapas", "potencial", "interesados", "sin_nota_5", "sin_nota_10", "sin_nota_30", "nuevo_3_notas",
	"sin_gestion", "recontacto", "sin_movimiento", "quitaron_seguimiento"];

/** Los filtros del celular, en botones (Esteban, 2026-10-13). "Nuevos / Recontacto Hoy" = entrada de hoy (creación o recontacto).
 * "Ventana Abierta" va de tercero (Esteban, 2026-10-16). */
const CHIPS_MOVIL: { key: string | null; label: string; alerta?: "rojo" | "oro" }[] = [
	{ key: null, label: "Todos" }, { key: "sin_atender", label: "Sin Atender", alerta: "rojo" }, { key: "ventana_abierta", label: "Ventana Abierta" },
	{ key: "promesas", label: "Promesas" }, { key: "vencidas", label: "Vencidos", alerta: "oro" }, { key: "manana", label: "Para Mañana" }, { key: "entradas_hoy", label: "Nuevos / Recontacto Hoy" },
];

/**
 * Conteos que NO salen de las celdas del aviso "conteos" (el servidor los cuenta aparte): siempre se preguntan.
 * (ProspectService::sumarVentanaYGestiones agrega "ventana_abierta" a las celdas antes de contar.)
 */
const FUERA_DE_CELDAS = ["ventana_abierta"];

/** Celdas del aviso "conteos" (16-oct-2026): {etapa: {clave: n}} de la dueña, como ProspectEstado::sumas([yo]). */
type Celdas = { [etapa: string]: { [clave: string]: number } };

/**
 * Los mismos números que ProspectService::countViewsDesdeConteos() con keys= (Mi cartera, sin crm): cada clave suma las
 * Etapas elegidas ([1] si no hay); "total" las elegidas (todas si no hay); "etapas" = total por Etapa (solo las > 0).
 */
const contarDeCeldas = (c: Celdas, etapas: number[], keys: string[]) => {
	const elegidas = etapas.filter((e) => e >= 1 && e <= 4);
	const todas = Object.keys(c).map(Number).filter((e) => e >= 1);
	const suma = (k: string, es: number[]) => es.reduce((n, e) => n + (Number(c[e]?.[k]) || 0), 0);
	const counts: { [k: string]: number } = {};
	keys.forEach((k) => { if (k !== "etapas") counts[k] = suma(k, k === "total" ? (elegidas.length ? elegidas : todas) : (elegidas.length ? elegidas : [1])); });
	const porEtapa: { [k: string]: number } = {};
	todas.forEach((e) => { const n = Number(c[e]?.total) || 0; if (n > 0) porEtapa[e] = n; });
	return { counts, etapas: porEtapa };
};

const mismasEtapas = (a: { [k: string]: number }, b: { [k: string]: number }) => {
	const ka = Object.keys(a).filter((k) => Number(a[k])), kb = Object.keys(b).filter((k) => Number(b[k]));
	return ka.length === kb.length && ka.every((k) => Number(a[k]) === Number(b[k]));
};

/** Mi cartera o lo del territorio (sin supervisión). */
const AlcanceSelect: React.FC<{ opciones: [string, string][]; value: string; onChange: (v: string) => void }> = ({ opciones, value, onChange }) => (
	<select value={opciones.some((o) => o[0] === value) ? value : "me"} onChange={(e) => onChange(e.target.value)} aria-label="Ver">
		{opciones.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
	</select>
);

/**
 * WappChat: la lista de conversaciones de Ventas y Admisiones. Arriba una sola línea (Esteban, 2026-10-05): área, Etapas
 * (casillas, todas por defecto), filtro y la lupa; la lista como WhatsApp, lo último primero, en tandas que llegan al bajar.
 * En computadora los filtros también van en el riel de la izquierda.
 */
const SalesController: React.FC<Props> = ({ inicial, onVerCrm, onSel, areas, area, onArea, numeros }) => {
	const http = HttpManager.getInstance();
	// El plantel de la BARRA, igual que Matrículas (2026-09-24); el de la sesión es respaldo.
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const legacyCampus: any = legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey] : null;
	const campusId: number | null = legacyCampus?.id ?? sessionCampus?.id ?? null;

	const [catalogs, setCatalogs] = useState<ProspectCatalogs | null>(null);
	const [etapas, setEtapasSt] = useState<number[]>(() => {
		const v = leer("wchat.etapas", "").split(",").map(Number).filter((x) => TODAS_ETAPAS.includes(x));
		return v.length ? v : TODAS_ETAPAS;
	});
	const setEtapas = (v: number[]) => { setEtapasSt(v); guardar("wchat.etapas", v.join(",")); };
	// Supervisión arranca en lo último que eligió (el servidor da "Mi cartera" a quien no supervisa): la lista no espera al catálogo.
	// Filtro "Plantel" (Todos mis planteles = null): estrecha la lista y los conteos a uno de mis planteles.
	const [plantel, setPlantel] = useState<number | null>(null);
	const [numero, setNumero] = useState<string>("");
	const [owner, setOwnerSt] = useState<string>(() => leer("wchat.owner", "all"));
	const setOwner = (v: string) => { setOwnerSt(v); guardar("wchat.owner", v); };
	const [view, setView] = useState<string | null>(null);
	const [search, setSearch] = useState<string>("");
	const [q, setQ] = useState<string>("");
	const [buscando, setBuscando] = useState<boolean>(false);
	const [counts, setCounts] = useState<{ [k: string]: number }>({});
	const [etapaCounts, setEtapaCounts] = useState<{ [k: string]: number }>({});
	const [rows, setRows] = useState<ProspectRow[]>([]);
	const [hasMore, setHasMore] = useState<boolean>(false);
	const [page, setPage] = useState<number>(1);
	const [sel, setSel] = useState<number | null>(inicial || null);
	useEffect(() => { if (onSel) onSel(sel); }, [sel]);
	// Resultado de "Mensajes" al buscar: abre a la persona en ese mensaje.
	const [irA, setIrA] = useState<{ prospectId: number; eventId: number; n: number } | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [nuevo, setNuevo] = useState<boolean>(false);
	const [nuevoChat, setNuevoChat] = useState<boolean>(false);
	const [telAlta, setTelAlta] = useState<string>("");
	const [menu, setMenu] = useState<boolean>(false);
	const [objetivo, setObjetivo] = useState<string>("");
	const [masFiltros, setMasFiltros] = useState<boolean>(false);

	useEffect(() => {
		if (!campusId) return;
		http.prospectsService.catalogs(campusId).then((r) => {
			setCatalogs(r.data);
			// Sin supervisión: su cartera, o lo del territorio si su rol lo trae (Kits arranca en sus matriculados; 2026-10-15).
			if (!r.data.isSupervisor) {
				const elegido = leer("wchat.alcance", "");
				const vale = elegido === "me" || (elegido === "kits" && r.data.kits) || (elegido === "territorio" && r.data.territorio);
				setOwner(vale ? elegido : r.data.kits ? "kits" : "me");
			}
		}).catch((e) => setError(errorTexto(e)));
	}, [campusId]);

	const etapa = etapaParam(etapas);
	// Buscar cruza Etapas: quien llama puede ser alumno, no solo prospecto.
	// Buscar no se queda en "Mis clientes": Supervisión busca en todo lo que ve (Esteban, 2026-10-06: el buscador no encontraba).
	const numIds = idsDeNumero(numeros, numero);
	const numLlave = numIds.join(",");
	const pl = { ...(plantel ? { planteles: [plantel] } : {}), ...(numIds.length ? { numeros: numIds } : {}) };
	const filtros = q ? { owner: catalogs?.isSupervisor ? "all" : owner, q, ...pl } : { etapa, owner, objetivo: objetivo || null, ...pl };
	const llave = JSON.stringify([campusId, filtros, q ? null : view]);

	// Solo pinta la respuesta más reciente: la lenta no debe pisar a la buena.
	const turnoConteos = useRef<number>(0);
	const turnoLista = useRef<number>(0);
	// Los conteos esperan a la lista: en un territorio grande cuentan cientos de miles y no deben retrasar lo que se ve primero.
	const listaLista = useRef<Promise<void>>(Promise.resolve());
	// Solo los conteos que se ven (Esteban, 2026-10-13: "tardan varios segundos"): en el celular los 5 botones; en la
	// computadora primero los rápidos (cada uno por su índice) y después, aparte, los pesados (total, Etapas, Potencial…).
	const masAbiertos = useRef<boolean>(false);
	const ultimaFirma = useRef<string>("");
	// Para decidir si los números salen del aviso sin preguntar (no son dependencias de cargarConteos: no deben recargarlo).
	const catalogsRef = useRef<ProspectCatalogs | null>(null);
	catalogsRef.current = catalogs;
	const qRef = useRef<string>("");
	qRef.current = q;
	const countsRef = useRef<{ [k: string]: number }>({});
	countsRef.current = counts;
	/**
	 * Las celdas del aviso valen para esta cartera solo después de comprobarlo una vez: la primera vez se pregunta igual y se
	 * compara clave por clave con lo que dice el servidor. Si cuadra, los siguientes avisos ya no preguntan. Si no (cubre a
	 * otra asesora, gestiones sobre fichas ajenas...), se sigue preguntando como antes. Precisión antes que ahorro.
	 */
	const celdasValen = useRef<{ firma: string; claves: string[] } | null>(null);
	/** Asesora "simple": sin supervisión, sin territorio ni Kits (gestiones sobre fichas ajenas) y sin bandejas compartidas. */
	const carteraSimple = () => {
		const cat = catalogsRef.current;
		const { yo, bandejas } = quienSoy();
		return !!cat && !cat.isSupervisor && !cat.kits && !cat.territorio && !!yo && bandejas === false;
	};
	const cargarConteos = useCallback(async (celdas?: Celdas | null) => {
		if (!campusId) return;
		const yo = ++turnoConteos.current;
		await listaLista.current;
		if (yo !== turnoConteos.current) return;
		const f = { etapa, owner, objetivo: objetivo || null, ...(plantel ? { planteles: [plantel] } : {}), ...(numLlave ? { numeros: numLlave.split(",").map(Number) } : {}) };
		// Otros filtros: los números de antes ya no valen (se llenan otra vez al llegar).
		const firma = JSON.stringify(f);
		if (firma !== ultimaFirma.current) { ultimaFirma.current = firma; setCounts({}); setEtapaCounts({}); celdasValen.current = null; }
		const cel = window.matchMedia?.("(max-width: 700px)").matches;
		const visibles = cel ? CHIPS_MOVIL.map((c) => c.key).filter(Boolean) as string[]
			: (() => { const fe = filtrosDeEtapas(etapas); return [...fe.top, ...(masAbiertos.current ? fe.mas : [])].map((x) => x.key).filter((k) => k !== "spam"); })();
		const rapidos = visibles.filter((k) => !CONTEOS_PESADOS.includes(k));
		const pesados = cel ? [] : ["total", "etapas", ...visibles.filter((k) => CONTEOS_PESADOS.includes(k))];
		const todas = Array.from(new Set([...rapidos, ...pesados]));
		// Las celdas del aviso solo sirven para "Mi cartera" estándar (sin objetivo, plantel ni búsqueda) de una asesora simple.
		const conCeldas = !!celdas && typeof celdas === "object" && owner === "me" && !objetivo && !plantel && !numLlave && !qRef.current && carteraSimple();
		const valen = conCeldas && celdasValen.current?.firma === firma ? celdasValen.current : null;
		try {
			if (valen && celdas) {
				// Sin preguntar: lo que sale de celdas, y en una sola petición lo que el servidor cuenta aparte (Ventana Abierta).
				const deCeldas = todas.filter((k) => k === "etapas" || valen.claves.includes(k));
				const d = contarDeCeldas(celdas, etapas, deCeldas);
				setCounts((c) => ({ ...c, ...d.counts }));
				if (deCeldas.includes("etapas")) setEtapaCounts(d.etapas);
				const faltan = todas.filter((k) => !deCeldas.includes(k));
				if (!faltan.length) return;
				const r = await http.prospectsService.views(campusId, { ...f, keys: faltan.join(",") } as any);
				if (yo !== turnoConteos.current) return;
				setCounts((c) => ({ ...c, ...(r.data.counts || {}) }));
				return;
			}
			// Mi cartera: una sola petición (con celdas, rápidos y pesados juntos cuestan lo mismo). Supervisión: rápidos primero.
			const juntos = owner === "me";
			const r = await http.prospectsService.views(campusId, { ...f, keys: (juntos ? todas : rapidos).join(",") } as any);
			if (yo !== turnoConteos.current) return;
			const recibidos: { [k: string]: number } = r.data.counts || {};
			// Si el respaldo trae otra cosa de lo que enseñaban las celdas, se vuelven a comprobar con el siguiente aviso.
			const antes = countsRef.current;
			if (!celdas && celdasValen.current && Object.keys(recibidos).some((k) => celdasValen.current!.claves.includes(k) && antes[k] !== undefined && Number(antes[k]) !== Number(recibidos[k]))) celdasValen.current = null;
			setCounts((c) => ({ ...c, ...recibidos }));
			if (juntos) {
				if (pesados.includes("etapas")) setEtapaCounts(r.data.etapas || {});
				if (conCeldas && celdas) {
					// Comprobación: ¿las celdas del aviso dan exactamente lo mismo que el servidor?
					const claves = Object.keys(recibidos).filter((k) => !FUERA_DE_CELDAS.includes(k));
					const d = contarDeCeldas(celdas, etapas, claves);
					const cuadra = claves.every((k) => Number(d.counts[k]) === Number(recibidos[k]))
						&& (!pesados.includes("etapas") || mismasEtapas(d.etapas, r.data.etapas || {}));
					celdasValen.current = cuadra ? { firma, claves } : null;
				}
				return;
			}
			if (!pesados.length) return;
			const r2 = await http.prospectsService.views(campusId, { ...f, keys: pesados.join(",") } as any);
			if (yo !== turnoConteos.current) return;
			setCounts((c) => ({ ...c, ...(r2.data.counts || {}) }));
			setEtapaCounts(r2.data.etapas || {});
		} catch (e) { setError(errorTexto(e)); }
	}, [campusId, etapa, owner, objetivo, plantel, numLlave]);

	/** Página 1 reemplaza (y se recuerda); las siguientes se agregan al final. */
	const cargarLista = useCallback((p: number) => {
		if (!campusId) return Promise.resolve();
		const yo = ++turnoLista.current;
		if (p === 1) {
			const m = memoria.get(llave);
			// Sin memoria (otra búsqueda u otro filtro) se limpia: la lista vieja seguía 3-4 s y un clic abría a otra persona (barrido 26-oct).
			if (m) { setRows(m.rows); setHasMore(m.hasMore); } else setRows([]);
		}
		setLoading(true); setError(null);
		const pedido = http.prospectsService.list(campusId, { ...filtros, view: q ? null : view, sinTotal: 1 }, POR_TANDA, p).then((r) => {
			if (yo !== turnoLista.current) return;
			const mas = r.data.hasMore ?? r.data.data.length >= POR_TANDA;
			if (p === 1) { setRows(r.data.data); memoria.set(llave, { rows: r.data.data, hasMore: mas }); }
			else setRows((prev) => [...prev, ...r.data.data.filter((x) => !prev.some((y) => y.id === x.id))]);
			setHasMore(mas); setPage(p);
		}).catch((e) => { if (yo === turnoLista.current) setError(errorTexto(e)); })
			.finally(() => { if (yo === turnoLista.current) setLoading(false); });
		listaLista.current = pedido;
		return pedido;
	}, [llave]);

	// Primero la lista, luego los conteos (el orden de estos efectos importa: cargarConteos espera a listaLista).
	useEffect(() => { cargarLista(1); }, [cargarLista]);
	useEffect(() => { setCounts({}); cargarConteos(); }, [cargarConteos]);
	// Al instante (wapp-push), sin preguntar de más (14-oct-2026: la lista y los conteos se pedían 60 mil veces por hora):
	// - "conteos": el servidor se lo manda SOLO a la dueña cuyos números cambiaron (mensaje suyo, status, nota, reasignación,
	//   o el reloj: Vencidas/Hoy). Desde 16-oct-2026 solo mueve los conteos (la lista ya llega por "wa") y trae las celdas
	//   (c): en "Mi cartera" de una asesora simple los números salen de ahí sin preguntar (ver cargarConteos).
	// - "wa" llega por cada mensaje y palomita de TODA la escuela: solo recarga si la persona ya está en la lista (mensaje
	//   nuevo: sube y cambia su texto) o es mía. Las palomitas no recargan nada. Con cartera ajena (supervisión, equipo)
	//   cualquier mensaje cuenta, pero a lo más una recarga cada 10 s.
	// - Por pestaña, a lo más unos conteos cada 1.5 s y una lista cada 2 s (los avisos seguidos se juntan).
	// - Respaldo: cada 5 min con avisos conectados (cada minuto sin ellos, y para supervisión: lo suyo no avisa por dueña).
	const rowsRef = useRef<ProspectRow[]>([]);
	rowsRef.current = rows;
	useEffect(() => {
		let tL: any, tC: any;
		let ultima = Date.now();      // últimos conteos (también para el respaldo)
		let ultimaLista = 0;
		let celdas: Celdas | null = null;
		const lista = (ms: number) => {
			if (tL) return;
			tL = setTimeout(() => {
				tL = null;
				if (document.visibilityState !== "visible") return;
				ultimaLista = Date.now(); cargarLista(1);
			}, Math.max(ms, 2000 - (Date.now() - ultimaLista)));
		};
		const conteos = (ms: number) => {
			if (tC) return;
			tC = setTimeout(() => {
				tC = null;
				if (document.visibilityState !== "visible") { celdas = null; return; }
				const c = celdas; celdas = null;
				ultima = Date.now(); cargarConteos(c);
			}, Math.max(ms, 1500 - (Date.now() - ultima)));
		};
		const offConteos = push.on("conteos", (d) => {
			// Se queda con las celdas más nuevas; si no trae (contadores no listos) se pregunta.
			celdas = d && d.c && typeof d.c === "object" ? d.c : null;
			conteos(300);
		});
		// Cartera ajena (supervisión, Super Admin): el servidor avisa "conteos_sup" (máx. 1 cada 2 s) cuando cambió algo de
		// cualquier dueña; solo los conteos (salen de celdas en 10-30 ms), juntando avisos de 3 s. La lista sigue por "wa".
		let tSup: any;
		const offSup = push.on("conteos_sup", () => {
			// Con "objetivo" los conteos no salen de celdas (recuento de segundos): ahí sigue el reloj.
			if (owner === "me" || objetivo || document.visibilityState !== "visible") return;
			clearTimeout(tSup);
			tSup = setTimeout(() => { ultima = Date.now(); cargarConteos(); }, 3000);
		});
		const off = push.on("wa", (d) => {
			if (!d || d.estado) return;
			const p = Number(d.p);
			const enLista = !!p && rowsRef.current.some((r) => r.id === p);
			const { yo, bandejas } = quienSoy();
			if (enLista || (yo && "o" in d && Number(d.o) === yo)) {
				lista(1500);
				// Mi cartera de asesora simple: sus números ya llegan con "conteos". Gestiones, bandejas o cartera ajena: aquí.
				if (owner !== "me" || bandejas !== false || !catalogsRef.current || catalogsRef.current.isSupervisor || catalogsRef.current.kits || catalogsRef.current.territorio) conteos(1500);
				return;
			}
			// Territorio de Kits/Admisiones (decenas de asesoras con la escuela entera a la vista): a lo más cada 30 s; lo que ya está
			// en su lista sube al momento por enLista.
			const cada = owner === "kits" || owner === "territorio" ? 30000 : 10000;
			if (owner !== "me") { const ms = Math.max(1500, cada - (Date.now() - ultima)); lista(ms); conteos(ms); }
		});
		const reloj = setInterval(() => {
			if (document.visibilityState !== "visible") return;
			if (Date.now() - ultima > (owner === "me" && push.conectado ? 290000 : 55000)) { ultima = Date.now(); cargarConteos(); }
		}, 30000);
		return () => { off(); offConteos(); offSup(); clearTimeout(tL); clearTimeout(tC); clearTimeout(tSup); clearInterval(reloj); };
	}, [cargarLista, cargarConteos, owner, objetivo]);
	// Buscar mientras se escribe (como WhatsApp): espera a que deje de teclear.
	useEffect(() => {
		const t = setTimeout(() => { const v = search.trim(); if (v !== q && (listoParaBuscar(v) || v === "")) { setQ(v); if (v) setSel(null); } }, 450);
		return () => clearTimeout(t);
	}, [search]);

	// La siguiente tanda cuando el final de la lista se asoma.
	const fin = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const el = fin.current;
		if (!el || !hasMore || loading) return;
		const io = new IntersectionObserver((e) => { if (e[0].isIntersecting) cargarLista(page + 1); }, { rootMargin: "400px" });
		io.observe(el);
		return () => io.disconnect();
	}, [hasMore, loading, page, cargarLista]);

	const refrescar = () => { cargarConteos(); cargarLista(1); };
	const irAVista = (e: number, v: string) => { setEtapas([e]); setView(v); setObjetivo(""); setSearch(""); setQ(""); setSel(null); };
	const objetivosEtapa = OBJETIVOS_BARRA.filter((o) => o.etapas.some((e) => etapas.includes(e)));
	const filtrosEtapa = filtrosDeEtapas(etapas);
	const verMas = masFiltros || filtrosEtapa.mas.some((f) => f.key === view) || !!objetivo;
	masAbiertos.current = verMas;
	// Al abrir "Más filtros" se piden sus conteos (antes venían siempre, aunque no se vieran).
	useEffect(() => { if (verMas) cargarConteos(); }, [verMas]);
	const elegirVista = (v: string | null) => { setView(v); setSearch(""); setQ(""); setSel(null); };
	// Mientras llegan los conteos (en frío un territorio tarda) no se enseña 0: se deja vacío.
	const cuenta = (k: string) => (k === "spam" || counts[k] === undefined ? "" : (counts[k] || 0).toLocaleString("es-MX"));
	const botonFiltro = (i: { key: string; label: string; alerta?: boolean; fuerte?: boolean }) => (
		<button key={i.key} className={"vista" + (!q && view === i.key ? " activa" : "")} onClick={() => elegirVista(i.key)}>
			<span>{i.label}</span>
			<span className={"cnt" + ((counts[i.key] || 0) > 0 && i.alerta ? " alerta" : (counts[i.key] || 0) > 0 && i.fuerte ? " fuerte" : "")}>{cuenta(i.key)}</span>
		</button>
	);

	if (!campusId) {
		return <div className="ventas"><div className="ventas-vacio">Elige un plantel en la barra de arriba.</div></div>;
	}

	const sup = !!catalogs?.isSupervisor;
	// Kits y la asesora de Admisiones (Esteban, 2026-10-15): además de su cartera, las conversaciones de las demás asesoras
	// con la gente de su territorio, para atenderlas (como en el CRM).
	const alcances: [string, string][] | null = catalogs?.kits ? [["me", "Mi Cartera"], ["kits", "Matriculados del Territorio"]]
		: catalogs?.territorio ? [["me", "Mi Cartera"], ["territorio", "Clientes del Territorio"]] : null;
	const etiquetaCartera = owner === "kits" ? "Todos los matriculados" : owner === "territorio" ? "Todo el territorio" : owner === "me" ? (sup ? "Mis chats" : "Todos mis chats") : owner === "none" ? "Sin asesora" : owner === "all" ? "Todos los chats"
		: owner.startsWith("team:") ? "Equipo " + owner.slice(5) : owner.startsWith("z:") ? (catalogs?.advisors.find((a) => "z:" + a.id === owner)?.name || "Asesora") : "Cartera";
	const conTotal = (n: number | undefined) => (n ? " (" + n.toLocaleString("es-MX") + ")" : "");
	const cerrarBusqueda = () => { setBuscando(false); setSearch(""); setQ(""); };

	return (
		<div className={"ventas" + (sel ? " con-cliente" : "")}>
			{catalogs?.cubriendo?.length ? (
				<div className="ventas-cubre">
					Estás cubriendo a {catalogs.cubriendo.map((c) => c.name + (c.hasta ? " (hasta el " + new Date(c.hasta + "T12:00:00").toLocaleDateString("es-MX", { day: "numeric", month: "short" }) + ")" : "")).join(" y a ")}: su gente está en tu cartera y en tus campañas.
				</div>
			) : null}
			<div className="ventas-cuerpo">
				<div className="ventas-riel">
					{/* Nuevo Chat (2026-10-17): busca el número; si no existe, abre el alta de prospecto con el teléfono puesto. */}
					<button className="ventas-nuevo" onClick={() => setNuevoChat(true)}>+ Nuevo Chat</button>
					{sup ? <div className="riel-cartera"><CarteraSelect catalogs={catalogs} value={owner} onChange={(v) => { setOwner(v); setSel(null); }} /></div> : null}
					{!sup && alcances ? <div className="riel-cartera"><AlcanceSelect opciones={alcances} value={owner} onChange={(v) => { setOwner(v); guardar("wchat.alcance", v); setSel(null); }} /></div> : null}
					<button className={"vista" + (!q && view === null ? " activa" : "")} onClick={() => elegirVista(null)}>
						<span style={{ fontWeight: 600 }}>{etiquetaCartera}</span>
						<span className="cnt">{counts.total !== undefined ? (counts.total || 0).toLocaleString("es-MX") : ""}</span>
					</button>
					<div className="grupo">Filtros</div>
					{filtrosEtapa.top.map(botonFiltro)}
					<button className="vista mas" aria-expanded={verMas} onClick={() => setMasFiltros(!verMas)}><span>{verMas ? "Menos filtros ▴" : "Más filtros ▾"}</span><span /></button>
					{verMas ? filtrosEtapa.mas.map(botonFiltro) : null}
					{verMas && objetivosEtapa.length ? (
						<div className="riel-objetivo">
							<select value={objetivo} onChange={(e) => { setObjetivo(e.target.value); setSel(null); }} aria-label="Objetivo">
								<option value="">Objetivo: todos</option>
								{objetivosEtapa.map((o) => <option key={o.key} value={o.key}>Objetivo: {o.label}</option>)}
							</select>
						</div>
					) : null}
				</div>

				<div className="ventas-lista">
					{/* Una sola línea: área, Etapas, filtro y la lupa (en el teléfono la lupa abre el buscador encima). */}
					{buscando ? (
						<div className="wl-barra buscando">
							<button className="wl-icono" onClick={cerrarBusqueda} aria-label="Cerrar búsqueda">←</button>
							<input type="text" enterKeyHint="search" placeholder="Buscar nombre, teléfono o mensaje" value={search} aria-label="Buscar" autoFocus
								onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { setQ(search.trim()); setSel(null); } if (e.key === "Escape") cerrarBusqueda(); }} />
							{search ? <button className="wl-icono" onClick={() => { setSearch(""); setQ(""); }} aria-label="Borrar">×</button> : null}
						</div>
					) : (
						<div className="wl-barra de-ventas">
							{/* En el celular estos tres se van al ⋮ y abajo quedan los botones de siempre (Esteban, 2026-10-13). */}
							<span className="solo-pc" style={{ display: "contents" }}>
							{areas && areas.length > 1 && onArea ? (
								<select className="wl-sel area" style={{ width: anchoArea(areas, area) }} value={area} onChange={(e) => onArea(e.target.value)} aria-label="Área">
									{areas.map((a) => <option key={a.key} value={a.key}>{a.label + (a.n ? " · " + a.n.toLocaleString("es-MX") : "")}</option>)}
								</select>
							) : null}
							<EtapasPicker value={etapas} onChange={(v) => { setEtapas(v); setView(null); setObjetivo(""); setSel(null); }} conteos={etapaCounts} />
							</span>
							<span className="wl-titulo-movil">{etiquetaCartera}</span>
							<select className="wl-sel filtro solo-pc" value={view || ""} aria-label="Filtro" onChange={(e) => elegirVista(e.target.value || null)}>
								<option value="">Todos</option>
								<optgroup label="Filtros">
									{filtrosEtapa.top.map((i) => <option key={i.key} value={i.key}>{i.label + conTotal(counts[i.key])}</option>)}
								</optgroup>
								<optgroup label="Más filtros">
									{filtrosEtapa.mas.map((i) => <option key={i.key} value={i.key}>{i.label + (i.key === "spam" ? "" : conTotal(counts[i.key]))}</option>)}
								</optgroup>
							</select>
							<input className="wl-busca-pc" type="search" placeholder="Buscar nombre, teléfono o mensaje" value={search} aria-label="Buscar"
								onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { setQ(search.trim()); setSel(null); } if (e.key === "Escape") { setSearch(""); setQ(""); } }} />
							<button className="wl-icono wl-lupa" onClick={() => setBuscando(true)} aria-label="Buscar">
								<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.2" /><path d="M15.5 15.5 21 21" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
							</button>
							<BotonNuevoChat enVentas onClick={() => setNuevoChat(true)} />
							<div className="wl-menu">
								<button className="wl-icono" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu(!menu)} aria-label="Más opciones">⋮</button>
								{menu ? <div className="wl-velo" onClick={() => setMenu(false)} aria-hidden="true" /> : null}
								{menu ? (
									<div className="cv-menu-lista" role="menu">
										<button role="menuitem" onClick={() => { setMenu(false); setNuevo(true); }}><b>+ Nuevo prospecto</b></button>
										{/* Celular: lo que antes iba en la barra. */}
										{areas && areas.length > 1 && onArea ? (
											<div className="wl-menu-cartera wl-solo-movil">
												<span>Área</span>
												<select value={area} onChange={(e) => { onArea(e.target.value); setMenu(false); }} aria-label="Área">
													{areas.map((a) => <option key={a.key} value={a.key}>{a.label + (a.n ? " · " + a.n.toLocaleString("es-MX") : "")}</option>)}
												</select>
											</div>
										) : null}
										<div className="wl-menu-cartera wl-solo-movil">
											<span>Etapas</span>
											<EtapasPicker value={etapas} onChange={(v) => { setEtapas(v); setView(null); setObjetivo(""); setSel(null); }} conteos={etapaCounts} />
										</div>
										<div className="wl-menu-cartera wl-solo-movil">
											<span>Otro filtro</span>
											<select value={view || ""} aria-label="Otro filtro" onChange={(e) => { elegirVista(e.target.value || null); setMenu(false); }}>
												<option value="">Todos</option>
												{[...filtrosEtapa.top, ...filtrosEtapa.mas].map((i) => <option key={i.key} value={i.key}>{i.label + (i.key === "spam" ? "" : conTotal(counts[i.key]))}</option>)}
											</select>
										</div>
										{(catalogs?.misPlanteles?.length || 0) > 1 ? (
											<div className="wl-menu-cartera wl-solo-movil">
												<span>Plantel</span>
												<PlantelSelect opciones={catalogs?.misPlanteles} value={plantel} onChange={(v) => { setPlantel(v); setSel(null); setMenu(false); }} />
											</div>
										) : null}
										{numeros?.length ? (
											<div className="wl-menu-cartera wl-solo-movil">
												<span>Número</span>
												<NumeroSelect numeros={numeros} value={numero} onChange={(v) => { setNumero(v); setSel(null); setMenu(false); }} />
											</div>
										) : null}
										{!sup && alcances ? (
											<div className="wl-menu-cartera">
												<span>Ver</span>
												<AlcanceSelect opciones={alcances} value={owner} onChange={(v) => { setOwner(v); guardar("wchat.alcance", v); setSel(null); setMenu(false); }} />
											</div>
										) : null}
										{sup ? (
											<div className="wl-menu-cartera">
												<span>Cartera</span>
												<CarteraSelect catalogs={catalogs} value={owner} onChange={(v) => { setOwner(v); setSel(null); setMenu(false); }} />
											</div>
										) : null}
										{objetivosEtapa.length ? (
											<div className="wl-menu-cartera">
												<span>Objetivo</span>
												<select value={objetivo} onChange={(e) => { setObjetivo(e.target.value); setSel(null); setMenu(false); }} aria-label="Objetivo">
													<option value="">Todos</option>
													{objetivosEtapa.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
												</select>
											</div>
										) : null}
									</div>
								) : null}
							</div>
						</div>
					)}
					{q ? <div className="wl-aviso">Buscando en todas las Etapas{sup && owner !== "all" ? " de " + etiquetaCartera.toLowerCase() : ""}</div>
						: sup && owner !== "all" ? <div className="wl-aviso">{etiquetaCartera}<button className="enlace" onClick={() => setOwner("all")}>Ver todos</button></div> : null}
					{/* Celular: los filtros de todos los días como botones (Esteban, 2026-10-13: "en vez de esos 4 filtros"). */}
					{!buscando ? (
						<div className="wl-chips" role="tablist" aria-label="Filtros">
							{CHIPS_MOVIL.map((c) => (
								<button key={c.key || "todos"} role="tab" aria-selected={!q && view === c.key} className={"wl-chip" + (!q && view === c.key ? " on" : "") + (c.alerta && (counts[c.key || ""] || 0) > 0 ? " " + c.alerta : "")}
									onClick={() => elegirVista(c.key)}>
									{c.label}{c.key && counts[c.key] ? <span className="n">{counts[c.key].toLocaleString("es-MX")}</span> : null}
								</button>
							))}
						</div>
					) : null}
					{!buscando && ((catalogs?.misPlanteles?.length || 0) > 1 || numeros?.length) ? (
						<div className="plantel-fila wl-solo-pc">
							<PlantelSelect opciones={catalogs?.misPlanteles} value={plantel} onChange={(v) => { setPlantel(v); setSel(null); }} />
							<NumeroSelect numeros={numeros} value={numero} onChange={(v) => { setNumero(v); setSel(null); }} />
						</div>
					) : null}
					{error ? (
						<div className="aviso error" style={{ margin: 10, display: "flex", alignItems: "center", gap: 10 }} role="alert">
							<span style={{ flex: 1 }}>{error}</span>
							{/* Sin quedarse en "Nada en…": se puede volver a pedir (barrido 26-oct, C1). */}
							<button type="button" className="btn-wa" onClick={() => { cargarLista(1); cargarConteos(); }}>Reintentar</button>
						</div>
					) : null}
					<div className="filas">
						{rows.map((r) => (
							<button key={r.id} className={"ventas-fila" + (sel === r.id ? " activa" : "") + (r.no_leidos ? " no-leido" : "")}
								onClick={() => { setSel(r.id); if (r.no_leidos) { setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, no_leidos: 0 } : x))); setTimeout(() => cargarConteos(), 1500); } }}>
								<span className={"av " + tonoAvatar(r.id)} aria-hidden="true">{iniciales(r)}</span>
								<span className="cuerpo">
									<span className="l1">
										<span className="nombre">{nombreLista(r) || nombreCompleto(r)}</span>
										<span className={"cuando" + (r.no_leidos ? " nuevo" : "")}>{cuando(r.last_activity_at || r.last_inbound_at)}</span>
									</span>
									<span className="l2">
										{(() => { const vp = vistaPrevia(r.last_message, r.last_message_by); return vp.texto ? <span className={"txt" + (vp.llamada ? " ll" + (vp.llamada === "perdida" ? " perd" : "") : "")}>{vp.llamada ? <WappIcono n={vp.llamada === "perdida" ? "phoneOff" : "phone"} /> : null}{vp.texto}</span> : <span className="txt">{[r.phone_e164 && (nombreLista(r) || nombreCompleto(r)) && !/^WhatsApp /.test(nombreLista(r) || nombreCompleto(r) || "") ? telefonoConLada(r.phone_e164) : null, interesesCortos(r.course_interest_text).join(", ")].filter(Boolean).join(" · ")}</span>; })()}
										{(() => { const pt = proximaTexto(diaMx(r.next_action_at), r.next_action_reason || null); return pt ? <span className={"pend" + (pt.vencida ? " vencida" : "")} title="Próxima actividad">{pt.texto}</span> : null; })()}
										{(r.no_leidos || 0) > 0 ? <span className="globo" aria-label={r.no_leidos + " sin leer"}>{r.no_leidos}</span>
											: r.no_leidos === -1 ? <span className="globo vacio" aria-label="Marcada como no leída" />
											: sinContestar(r) ? <span className="punto hueco" title="Sin contestar" aria-label="Sin contestar" /> : null}
									</span>
									{/* Tercera línea chiquita (Esteban, 2026-10-05: las etiquetas se salían y tapaban el mensaje): Etapa · status · asesora · gestión · WAPP. */}
									<span className="l3 meta">
										{etapas.length > 1 || q ? <span>{ETAPAS.find((e) => e.id === r.etapa_id)?.name}</span> : null}
										<span className={"st " + statusClase(r.status_id)}>{r.status}</span>
										{owner !== "me" && r.owner_zoho_name ? <span>{r.owner_zoho_name.split(" ")[0]}</span> : null}
										{(r.gestiones || []).slice(0, 1).map((g) => <span key={g.id} className="obj" title={g.owner ? "a cargo de " + g.owner : undefined}>{g.code === "promo" ? "Promo" : g.name}</span>)}
										{r.wapp ? <span>{resumenWapp(r.wapp)}</span> : null}
									</span>
								</span>
							</button>
						))}
						{!loading && !rows.length && !error ? <div className="ventas-vacio">{q ? "Ninguna persona con ese nombre o teléfono." : "Nada en " + (CHIPS_MOVIL.find((c) => c.key && c.key === view)?.label || etiquetaFiltros(etapas, view)).toLowerCase() + "."}</div> : null}
						<div ref={fin} className="wl-fin">{loading ? <span className="wl-cargando" aria-label="Cargando" /> : null}</div>
						{/* Como WhatsApp: debajo de las personas, las conversaciones que dicen lo buscado (mismo alcance que la lista). */}
						{q ? <MensajesEncontrados q={q} llave={JSON.stringify([campusId, filtros.owner, plantel])} activo={sel}
							pedir={(t) => http.prospectsService.buscarMensajes(campusId, { owner: filtros.owner, planteles: plantel ? [plantel] : undefined }, t).then((r) => r.data.resultados)}
							onAbrir={(r) => { setSel(r.prospect_id); setIrA({ prospectId: r.prospect_id, eventId: r.event_id, n: Date.now() }); }} /> : null}
					</div>
				</div>

				{sel ? <ClienteView campusId={campusId} prospectId={sel} catalogs={catalogs} onChanged={refrescar} onOpen={setSel} onVista={irAVista} onVerCrm={onVerCrm} irAEvento={irA}
					volverTexto={etiquetaCartera} onBack={() => setSel(null)} />
					: <div className="ventas-cliente"><div className="ventas-vacio">Elige una conversación de la lista.</div></div>}
			</div>

			{nuevoChat ? (
				<NuevoChat campusId={campusId} dept="ventas" onClose={() => setNuevoChat(false)}
					onAbrir={(id) => { setNuevoChat(false); setSel(id); }}
					onAlta={(tel) => { setNuevoChat(false); setTelAlta(tel); setNuevo(true); }} />
			) : null}
			{nuevo ? (
				<NewProspectModal campusId={campusId} catalogs={catalogs} telefonoInicial={telAlta || undefined} onClose={() => { setNuevo(false); setTelAlta(""); }}
					onCreated={(id) => { setNuevo(false); setTelAlta(""); setSel(id); refrescar(); }}
					onOpenExisting={(id) => { setNuevo(false); setTelAlta(""); setSel(id); }} />
			) : null}
		</div>
	);
};

export default SalesController;
